import {
  Box,
  Divider,
  List,
  ListItem,
  Tooltip,
  Typography,
  AppBar,
  Tab,
  Tabs,
  withStyles,
  IconButton
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AssessmentIcon from '@material-ui/icons/Assessment';
import BusinessIcon from '@material-ui/icons/Business';
import 'leaflet-rotatedmarker';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { INFOR_MAG4G, INFOR_CAR, INFOR_MOTO } from 'src/app/constant/config';
import ActivityChartModal from './ActivityChartModal';
import { formatMemoryStatus } from './memoryStatus';
import { LIVE_WS_FIELDS, hasLiveValue } from './liveFields';
import { offlineLabelOf, staleValueNote } from 'src/app/utils/offlineStatus';

const BASIC_FIELDS = [
  'device_id',
  'device_type',
  'device_status',
  'license_plate',
  'lat',
  'lng',
  'vehicle_type',
  'created_at',
  'custom_name',
  'coordinates',
  'activity_chart',
  'fuel_sensor_val'
];

const FIXED_FIELDS = [
  'license_plate',
  'serial',
  'device_type',
  'imei_sim',
  'sim',
  'network_operator',
  'active_at',
  'status_transmission',
  'firmware_version',
  'hardware_version',
];

const CALCULATED_FIELDS = ['remaining_days'];



const INFO_BOX_MAP = {
  VGPS_MOTO4G: INFOR_MOTO,
  VGPS_MAG4G: INFOR_MAG4G,
  VGPS_CAR4G: INFOR_CAR,
  VGPS_2CAM: INFOR_CAR,
  VGPS_4CAM: INFOR_CAR
};

const LocationDisplay = ({ address }) => {
  if (!address || address === '--') {
    return (
      <div style={{ fontSize: '12px', float: 'right', textAlign: 'right' }}>
        --
      </div>
    );
  }

  return (
    <Tooltip title={address} arrow>
      <div
        style={{
          fontSize: '12px',
          textAlign: 'right',
          maxWidth: '200px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.3',
          overflowWrap: 'anywhere'
        }}
      >
        {address}
      </div>
    </Tooltip>
  );
};

const LicensePlateDisplay = ({ Plate }) => {
  const maxLength = 20;
  const truncatedPlate =
    Plate && Plate.length > maxLength
      ? `${Plate.substring(0, maxLength)}...`
      : Plate
        ? Plate
        : '--';

  return (
    <Tooltip title={Plate || ''} arrow>
      <div style={{ fontSize: '12px', float: 'right', textAlign: 'right' }}>
        {truncatedPlate}
      </div>
    </Tooltip>
  );
};

const CoordinatesDisplay = ({ coords }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (coords && coords !== '--') {
      navigator.clipboard.writeText(coords);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: '12px' }}>
      <span>{coords}</span>
      {coords !== '--' && (
        <Tooltip title={copied ? "Đã copy!" : "Copy tọa độ"} arrow>
          <FileCopyIcon
            style={{ fontSize: '14px', marginLeft: '6px', cursor: 'pointer', color: '#1976d2' }}
            onClick={handleCopy}
          />
        </Tooltip>
      )}
    </div>
  );
};

const Level4GlAndsatellitesDisplay = ({
  level_4g,
  gsm_signal,
  satellites
}) => {
  const signal = gsm_signal ?? '--';
  const satelliteCount = satellites ?? '--';

  return (
    <Typography>
      <div style={{ fontSize: '12px' }}>
        {signal} / {satelliteCount}
      </div>
    </Typography>
  );
};

const DisplayGsmColumn = ({ detailedInfo }) => {
  const level = detailedInfo?.gsm_level || 0;
  return (
    <img
      src={`/static/iconSvg/gsm/${level}_column.svg`}
      style={{
        paddingRight: '5px',
        marginLeft: '0 !important'
      }}
    />
  );
};

const TabPanel = props => {
  const { children, value, index, style, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ height: 'auto', minHeight: '380px', ...style }}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

const CustomTab = withStyles({
  root: {
    backgroundColor: '#F6F6F6',
    color: '#475461',
    fontSize: '13px',
    padding: '0 4px',
    whiteSpace: 'nowrap',
  },
  selected: {
    backgroundColor: '#ffffff',
    color: '#D9042F',
    fontWeight: 600,
  }
})(Tab);

const ContentCarInfo = ({
  positionsInfoBox: originalPositionsInfoBox,
  detailedVehicleInfo,
  isLoadingDetail
}) => {
  const positionsInfoBox = { ...(originalPositionsInfoBox?.vehicle || {}), ...(originalPositionsInfoBox || {}) };
  const deviceType = positionsInfoBox?.device_type;
  const isMotorGPS = deviceType === 'VGPS_MOTO4G';
  const isMag4G = deviceType === 'VGPS_MAG4G';

  const vehicleInfoBox = INFO_BOX_MAP[deviceType] ?? INFOR_CAR;

  const [value, setValue] = React.useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [hasShownInitialLoading, setHasShownInitialLoading] = useState(false);
  const [openChart, setOpenChart] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (positionsInfoBox?.device_id && !hasShownInitialLoading) {
      setIsInitialLoading(true);
      setHasShownInitialLoading(true);
    }
  }, [positionsInfoBox?.device_id, hasShownInitialLoading]);

  useEffect(() => {
    if (!isLoadingDetail && isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [isLoadingDetail, isInitialLoading]);

  useEffect(() => {
    if ((isMotorGPS || isMag4G) && value === 2) {
      setValue(0);
    }
  }, [isMotorGPS, isMag4G, value]);

  const getRemainingDays = endDate => {
    if (!endDate) {
      return '--';
    }
    try {
      const now = moment();
      const expiration = moment(endDate);
      if (!expiration.isValid()) {
        return '--';
      }
      if (expiration.isBefore(now)) {
        return 'Hết hạn';
      }
      const duration = moment.duration(expiration.diff(now));
      const days = Math.floor(duration.asDays());
      if (days >= 1) {
        return `${days} Ngày`;
      }
      const hours = Math.floor(duration.asHours());
      if (hours >= 1) {
        return `${hours} Giờ`;
      }
      const hoursRemaining = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes()) % 60;
      const seconds = Math.floor(duration.asSeconds()) % 60;
      return `${hoursRemaining
        .toString()
        .padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } catch (error) {
      return '--';
    }
  };

  const [fixedValues, setFixedValues] = useState({});

  useEffect(() => {
    const deviceId = positionsInfoBox?.device_id;
    if (!deviceId) {
      setFixedValues({});
      return;
    }
    const newFixedValues = {};
    FIXED_FIELDS.forEach(field => {
      let value;
      if (field === 'imei_sim' || field === 'sim') {
        value =
          detailedVehicleInfo && detailedVehicleInfo[field] !== undefined
            ? detailedVehicleInfo[field]
            : undefined;
      } else {
        value =
          positionsInfoBox && positionsInfoBox[field] !== undefined
            ? positionsInfoBox[field]
            : detailedVehicleInfo && detailedVehicleInfo[field] !== undefined
              ? detailedVehicleInfo[field]
              : undefined;
      }
      if (value !== undefined) {
        newFixedValues[field] = value;
      }
    });
    setFixedValues(newFixedValues);
  }, [positionsInfoBox?.device_id, detailedVehicleInfo]);

  const renderValueInWindowBox = infor => {
    //from api get detail
    const safeDetailedVehicleInfo = detailedVehicleInfo || {};
    const safePositionsInfoBox = positionsInfoBox || {};

    const valueSource = FIXED_FIELDS.includes(infor.dataKey)
      ? { [infor.dataKey]: fixedValues[infor.dataKey] }
      : LIVE_WS_FIELDS.includes(infor.dataKey) &&
        hasLiveValue(safePositionsInfoBox, infor.dataKey)
        ? safePositionsInfoBox
        : safeDetailedVehicleInfo[infor.dataKey] !== undefined
          ? safeDetailedVehicleInfo
          : safePositionsInfoBox;
    if (
      !FIXED_FIELDS.includes(infor.dataKey) &&
      !BASIC_FIELDS.includes(infor.dataKey) &&
      isInitialLoading
    ) {
      return <Skeleton variant="text" width={40} />;
    }
    if (
      !BASIC_FIELDS.includes(infor.dataKey) &&
      !CALCULATED_FIELDS.includes(infor.dataKey) &&
      safeDetailedVehicleInfo[infor.dataKey] === undefined &&
      !isLoadingDetail
    ) {
      return '--';
    }
    switch (infor.dataKey) {
      case 'created_at': {
        if (!safePositionsInfoBox[infor.dataKey]) return '--';
        const at = moment(safePositionsInfoBox[infor.dataKey] * 1000).format(
          'HH:mm:ss DD/MM/YYYY'
        );
        return at;
      }
      case 'active_at': {
        const activeAt = valueSource[infor.dataKey];
        const dateDebt =
          safeDetailedVehicleInfo.date_debt ??
          safePositionsInfoBox.date_debt;
        const remainingDaysFromApi =
          safeDetailedVehicleInfo.remaining_days ??
          safePositionsInfoBox.remaining_days ??
          safeDetailedVehicleInfo.deviceServicePackages?.remaining_days ??
          safePositionsInfoBox.deviceServicePackages?.remaining_days;
        const remainingDays =
          remainingDaysFromApi ??
          (dateDebt && moment(dateDebt).isValid()
            ? Math.max(moment(dateDebt).diff(moment(), 'days'), 0)
            : null);

        if (!activeAt) {
          return '--';
        }

        const formattedActiveAt = moment(activeAt).format('DD/MM/YYYY');
        return remainingDays != null
          ? `${formattedActiveAt} (Còn: ${remainingDays} ngày)`
          : formattedActiveAt;
      }
      case 'speed_gps': {
        if (safePositionsInfoBox[infor.dataKey] == null) return '--';
        const speed = `${Math.floor(safePositionsInfoBox[infor.dataKey])} Km/h`;
        return speed;
      }
      case 'engine_status':
        return valueSource[infor.dataKey] != null
          ? valueSource[infor.dataKey]
            ? 'Bật'
            : 'Tắt'
          : '--';
      case 'max_speed':
        return valueSource[infor.dataKey] != null
          ? `${Math.floor(valueSource[infor.dataKey])} Km/h`
          : '--';
      case 'daily_distance':
        return valueSource[infor.dataKey] != null
          ? `${valueSource[infor.dataKey]} Km`
          : '--';
      case 'total_distance':
        return valueSource[infor.dataKey] != null
          ? `${valueSource[infor.dataKey]} Km`
          : '--';
      case 'air_condition_status':
        return valueSource[infor.dataKey] != null
          ? valueSource[infor.dataKey]
            ? 'Bật'
            : 'Tắt'
          : '--';
      case 'memory_card':
        return formatMemoryStatus(valueSource[infor.dataKey]);
      case 'is_stopping':
        return valueSource[infor.dataKey] ? 'Đang đỗ' : '--';
      case 'remaining_days':
        const dateDebt =
          safeDetailedVehicleInfo.date_debt !== undefined
            ? safeDetailedVehicleInfo.date_debt
            : safePositionsInfoBox.date_debt;
        return getRemainingDays(dateDebt);
      case 'battery':
        return valueSource[infor.dataKey] != null
          ? `${valueSource[infor.dataKey]} ${isMag4G ? '%' : 'V'}`
          : '--';
      case 'location':
        return (
          <LocationDisplay address={`${valueSource[infor.dataKey] || ''}`} />
        );
      case 'serial':
        return valueSource[infor.dataKey] || '--';
      case 'imei_sim':
        return valueSource[infor.dataKey] || '--';
      case 'firmware_version': {
        const firmwareVersion = valueSource[infor.dataKey];
        const hardwareVersion =
          fixedValues.hardware_version ??
          safeDetailedVehicleInfo.hardware_version ??
          safePositionsInfoBox.hardware_version;

        if (!firmwareVersion) {
          return '--';
        }

        return `${hardwareVersion || '--'} / ${firmwareVersion}`;
      }
      case 'gsm_level':
        return (
          <Level4GlAndsatellitesDisplay
            level_4g={valueSource[infor.dataKey]}
            gsm_signal={
              hasLiveValue(safePositionsInfoBox, 'gsm_signal')
                ? safePositionsInfoBox.gsm_signal
                : safeDetailedVehicleInfo.gsm_signal
            }
            satellites={
              hasLiveValue(safePositionsInfoBox, 'satellites')
                ? safePositionsInfoBox.satellites
                : safeDetailedVehicleInfo.satellites
            }
          />
        );
      case 'charge_status':
        return valueSource[infor.dataKey] === 1 ? 'Đang sạc' : 'Không sạc';
      case 'fuel_sensor_val': {
        const level = safeDetailedVehicleInfo.oil_level ?? safePositionsInfoBox.oil_level;
        const type = safeDetailedVehicleInfo.oil_type ?? safePositionsInfoBox.oil_type;
        if (level == null) return '--';
        return type ? `${level} %` : level;
      }
      case 'license_plate': {
        const plate = valueSource.license_plate || '--';
        return <LicensePlateDisplay Plate={plate} />;
      }
      case 'custom_name': {
        const name = safePositionsInfoBox.name || safePositionsInfoBox.vehicle?.name;
        const displayText = name?.trim() ? name : '--';
        return <LicensePlateDisplay Plate={displayText} />;
      }
      case 'activity_chart':
        const oilCap = safeDetailedVehicleInfo.oil_cap ?? safePositionsInfoBox.oil_cap;
        const oilMap = safeDetailedVehicleInfo.oil_map ?? safePositionsInfoBox.oil_map;
        const oilMaxCap = Array.isArray(oilMap) && oilMap.length
          ? oilMap[oilMap.length - 1]?.v
          : null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
            {oilCap != null && (
              <span>
                {oilMaxCap != null ? `${oilCap}/${oilMaxCap}` : oilCap} L
              </span>
            )}
            <Tooltip title="Xem Cảm biến nhiên liệu" arrow>
              <IconButton
                size="small"
                onClick={() => setOpenChart(true)}
                style={{
                  padding: '2px',
                  color: '#0c5299',
                  backgroundColor: '#e8f0fe',
                  borderRadius: '4px',
                  border: '1px solid #0c5299'
                }}
              >
                <AssessmentIcon style={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      case 'coordinates': {
        const lat = safePositionsInfoBox.lat;
        const lng = safePositionsInfoBox.lng;
        const coords = (lat != null && lng != null) ? `${lat}, ${lng}` : '--';
        return <CoordinatesDisplay coords={coords} />;
      }
      case 'driver_license': {
        const license = valueSource[infor.dataKey];
        const driverName = valueSource.driver_name;
        if (license === 'VGPS' || driverName === 'Lái xe đăng xuất') {
          return '--';
        }
        return license || '--';
      }
      case 'status_transmission': {
        const status = valueSource[infor.dataKey];
        return status === 1 ? 'Bật' : 'Tắt';
      }
      
      default:
        return valueSource[infor.dataKey] ?? '--';
    }
  };

  return (
    <>
      <Box
        style={{
          width: '300px',
          height: 'auto'
        }}
      >
        <div style={{ flexGrow: 1, paddingTop: '6px' }}>
          <TabPanel
            value={value}
            index={0}
            style={{ height: 'auto', minHeight: '160px' }}
          >
            <List
              component="nav"
              style={{
                padding: '0'
              }}
            >
              {vehicleInfoBox[0]
                ?.filter(infor => {
                  if (infor.dataKey === 'activity_chart') {
                    const oilMap = positionsInfoBox?.oil_map || detailedVehicleInfo?.oil_map;
                    return Array.isArray(oilMap) && oilMap.length > 1;
                  }
                  return true;
                })
                .map((infor, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      disableGutters
                      style={{
                        padding: '4px 10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}
                    >
                      <div
                        style={{
                          float: 'left',
                          fontSize: '12px',
                          flexShrink: 0,
                          paddingTop: '2px'
                        }}
                      >
                        <img
                          src={`/static/iconSvg/${infor.dataKey === 'activity_chart' ? 'fuel_sensor' : infor.icon}.svg`}
                          style={{
                            paddingRight: '5px',
                            marginLeft: '0 !important'
                          }}
                        />
                        <b> {infor.label} </b>
                      </div>

                      <div
                        style={{
                          float: 'right',
                          textAlign: 'right',
                          fontSize: '12px',
                          paddingLeft: '10px'
                        }}
                      >
                        {renderValueInWindowBox(infor)}
                      </div>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
            </List>
          </TabPanel>
          <TabPanel
            value={value}
            index={1}
            style={{ height: 'auto', minHeight: '160px' }}
          >
            {vehicleInfoBox[1]
              ?.filter(infor => {
                if (infor.dataKey === 'fuel_sensor_val') {
                  return ['VGPS_CAR4G', 'VGPS_2CAM', 'VGPS_4CAM'].includes(deviceType);
                }
                return true;
              })
              ?.map((infor, index) => (
              <React.Fragment key={index}>
                <ListItem
                  disableGutters
                  style={{
                    padding: '4px 10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <div
                    style={{
                      float: 'left',
                      fontSize: '12px',
                      flexShrink: 0,
                      paddingTop: '2px'
                    }}
                  >
                    {infor.dataKey === 'gsm_level' ? (
                      // Cột sóng cũng phải theo đường WebSocket, nếu không biểu tượng và con số
                      // ngay cạnh nó đọc từ hai nguồn lệch nhau tới 30 giây.
                      <DisplayGsmColumn
                        detailedInfo={
                          hasLiveValue(positionsInfoBox, 'gsm_level')
                            ? positionsInfoBox
                            : detailedVehicleInfo
                        }
                      />
                    ) : infor.dataKey === 'agency_name' ? (
                      <BusinessIcon
                        style={{
                          fontSize: '18px',
                          color: '#D9042F',
                          paddingRight: '5px'
                        }}
                      />
                    ) : (
                      <img
                        src={`/static/iconSvg/${infor.icon}.svg`}
                        style={{
                          paddingRight: '5px',
                          marginLeft: '0 !important'
                        }}
                      />
                    )}
                    <b> {infor.label} </b>
                  </div>

                  <div
                    style={{
                      float: 'right',
                      textAlign: 'right',
                      fontSize: '12px',
                      paddingLeft: '10px'
                    }}
                  >
                    {renderValueInWindowBox(infor)}
                  </div>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </TabPanel>
          <TabPanel
            value={value}
            index={2}
            style={{ minHeight: '160px' }}
          >
            {vehicleInfoBox[2]?.map((infor, index) => (
              <React.Fragment key={index}>
                <ListItem
                  disableGutters
                  style={{
                    padding: '4px 10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <div
                    style={{
                      float: 'left',
                      fontSize: '12px',
                      flexShrink: 0,
                      paddingTop: '2px'
                    }}
                  >
                    <img
                      src={`/static/iconSvg/${infor.icon}.svg`}
                      style={{
                        paddingRight: '5px',
                        marginLeft: '0 !important'
                      }}
                    />
                    <b> {infor.label} </b>
                  </div>

                  <div
                    style={{
                      float: 'right',
                      textAlign: 'right',
                      fontSize: '12px',
                      paddingLeft: '10px'
                    }}
                  >
                    {renderValueInWindowBox(infor)}
                  </div>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </TabPanel>

          <AppBar
            position="static"
            style={{
              backgroundColor: '#ffffff',
              boxShadow: 'none',
              borderRadius: '15px'
            }}
          >
            <Tabs
              value={value}
              variant="fullWidth"
              onChange={handleChange}
              centered
              aria-label="simple tabs example"
              style={{
                height: '35px',
                minHeight: '35px',
                width: '100%',
                borderRadius: '0px 0px 15px 15px'
              }}
              TabIndicatorProps={{ hidden: true }}
            >
              <CustomTab
                label="Phương tiện"
                {...a11yProps(0)}
                style={{
                  textTransform: 'none',
                  height: '100%',
                  minHeight: '35px',
                  minWidth: '80px'
                }}
              />
              <CustomTab
                label="Thiết bị"
                {...a11yProps(1)}
                style={{
                  textTransform: 'none',
                  height: '100%',
                  minHeight: '35px',
                  minWidth: '80px'
                }}
              />
              {!isMotorGPS && !isMag4G && (
                <CustomTab
                  label="Lái xe"
                  {...a11yProps(2)}
                  style={{
                    textTransform: 'none',
                    height: '100%',
                    minHeight: '35px',
                    minWidth: '80px'
                  }}
                />
              )}
            </Tabs>
          </AppBar>
        </div>
      </Box>
      <ActivityChartModal
        open={openChart}
        handleClose={() => setOpenChart(false)}
        defaultVehicle={positionsInfoBox}
      />
    </>
  );
};

export default React.memo(ContentCarInfo);
